/* eslint-disable react-refresh/only-export-components */
// Fast Refresh warning suppressed: Standard React context pattern exports context, provider, and hook together
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '../lib/utils/logger';

export type SidebarMode = 'hidden' | 'minimal' | 'expanded';

interface SidebarContextValue {
  mode: SidebarMode;
  setMode: (mode: SidebarMode) => void;
  isExpanded: boolean;
  isHovering: boolean;
  setIsHovering: (hovering: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

export const SIDEBAR_STORAGE_KEY = 'sidebar-mode';
export const SIDEBAR_STORAGE_KEY_ADMIN = 'sidebar-mode-admin';
const DEFAULT_STORAGE_KEY = SIDEBAR_STORAGE_KEY;

interface SidebarProviderProps {
  children: React.ReactNode;
  /** localStorage key for persisting sidebar mode (defaults to 'sidebar-mode') */
  storageKey?: string;
}

/**
 * Provider for sidebar state that ensures all components share the same state
 *
 * Manages sidebar display mode with localStorage persistence (client-side only):
 * - hidden: Fully collapsed until hover
 * - minimal: Shows only icons
 * - expanded: Pinned open with full labels
 *
 * Use different storageKey values to isolate sidebar state between portals.
 */
export function SidebarProvider({
  children,
  storageKey = DEFAULT_STORAGE_KEY,
}: SidebarProviderProps) {
  // Initialize from localStorage or default to expanded (purely client-side)
  const [mode, setModeState] = useState<SidebarMode>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && ['hidden', 'minimal', 'expanded'].includes(stored)) {
        return stored as SidebarMode;
      }
    } catch (error) {
      logger.warn('Failed to read sidebar state from localStorage', error);
    }
    return 'expanded';
  });

  const [isHovering, setIsHovering] = useState(false);

  // Persist to localStorage when mode changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, mode);
    } catch (error) {
      logger.warn('Failed to save sidebar state to localStorage', error);
    }
  }, [mode, storageKey]);

  const setMode = useCallback((newMode: SidebarMode) => {
    setModeState(newMode);
    setIsHovering(false); // Reset hover state when mode changes
  }, []);

  const value: SidebarContextValue = useMemo(
    () => ({
      mode,
      setMode,
      isExpanded: mode === 'expanded',
      isHovering,
      setIsHovering,
    }),
    [mode, setMode, isHovering, setIsHovering],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

/**
 * Hook to access sidebar state from any component
 *
 * Must be used within a SidebarProvider
 */
export function useSidebarContext() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
}
